import type { AudioManager, AudioManagerOptions, SfxKey } from './AudioManager';

export interface LazyAudio {
  init(): Promise<void>;
  playSfx(key: SfxKey): void;
  startMusic(): void;
  stopMusic(): void;
  setMuted(muted: boolean): void;
  setMasterVolume(volume: number): void;
  resumeOnUserGesture(): void;
  dispose(): void;
}

export function createLazyAudio(options: AudioManagerOptions): LazyAudio {
  let inner: AudioManager | null = null;
  let pendingInit: Promise<void> | null = null;
  let mutedQueued: boolean | null = null;
  let volumeQueued: number | null = null;
  let musicQueued = false;
  let resumeQueued = false;
  let disposed = false;

  const ensureInit = (): Promise<void> => {
    // React StrictMode dev double-invoke: cleanup runs before init, leaving us
    // disposed with no real teardown to undo. Reset and proceed.
    if (disposed && inner === null && pendingInit === null) disposed = false;
    if (pendingInit) return pendingInit;
    if (disposed) return Promise.resolve();
    pendingInit = (async () => {
      const mod = await import('./AudioManager');
      if (disposed) return;
      inner = new mod.AudioManager(options);
      await inner.init();
      if (disposed) {
        inner.dispose();
        inner = null;
        return;
      }
      if (mutedQueued !== null) inner.setMuted(mutedQueued);
      if (volumeQueued !== null) inner.setMasterVolume(volumeQueued);
      if (resumeQueued) inner.resumeOnUserGesture();
      if (musicQueued) inner.startMusic();
    })();
    return pendingInit;
  };

  return {
    init: ensureInit,
    playSfx(key) {
      inner?.playSfx(key);
    },
    startMusic() {
      if (inner) inner.startMusic();
      else musicQueued = true;
    },
    stopMusic() {
      musicQueued = false;
      inner?.stopMusic();
    },
    setMuted(muted) {
      mutedQueued = muted;
      inner?.setMuted(muted);
    },
    setMasterVolume(volume) {
      volumeQueued = volume;
      inner?.setMasterVolume(volume);
    },
    resumeOnUserGesture() {
      if (inner) inner.resumeOnUserGesture();
      else resumeQueued = true;
    },
    dispose() {
      disposed = true;
      inner?.dispose();
      inner = null;
    },
  };
}
